import { ActionTree } from 'vuex'
import axios from 'axios'

import _get from 'lodash/get'
import _find from 'lodash/find'

import { BASE_URL } from '@/config'
import { OfferCartsState } from './types'
import { RootState } from '../types'
import { Seller, Offer, OfferItem } from '@/types'

export const actions: ActionTree<OfferCartsState, RootState> = {
  getOffers({ commit }, payload) {
    const path = `${BASE_URL}/offer-carts`
    const params = {
      ...payload,
    }

    return axios
      .get(path, { params })
      .then(response => {
        const data = _get(response, 'data') || []
        return data
      })
      .catch(err => {
        throw err
      })
  },
  getActiveOffer(
    { commit, getters, rootGetters },
    payload: {
      userId: string
      sellerId: string
      sellerUserId: string
      buyerUserId: string
      offerItems: [{ bookId: string; offerPrice: number; quantity: number; notes?: string }]
    }
  ) {
    return new Promise((resolve, reject) => {
      const activeOffer = rootGetters['offerCarts/getActiveOffer']

      if (
        _get(activeOffer, ['sellerId']) === payload.sellerId &&
        _get(activeOffer, ['sellerUserId']) === payload.sellerUserId &&
        _get(activeOffer, ['buyerUserId']) === payload.buyerUserId
      ) {
        resolve(activeOffer)
      } else {
        commit('setRequestPostOffer', true)
        const path = `${BASE_URL}/offer-carts`
        axios
          .post(path, {
            ...payload,
            userId: _get(rootGetters['auth/getUserInfo'], 'id'),
          })
          .then(response => {
            const tempOffer = _get(response, 'data')
            commit('resetRequest')
            commit('setActiveOffer', { offer: tempOffer })
            commit('sellers/setActiveSeller', { seller: _get(tempOffer, 'seller') }, { root: true })
            resolve(tempOffer)
          })
          .catch(err => {
            commit('setRequestPostOffer', false)
            reject(err)
          })
      }
    })
  },
  getOffer({ commit }, payload: string) {
    // offerId
    const path = `${BASE_URL}/offer-carts`
    const params = {
      filter: { id: payload },
    }
    return axios
      .get(path, { params })
      .then(response => {
        const tempOffer = _get(response, 'data')
        commit('setActiveOffer', { offer: tempOffer })
        commit('sellers/setActiveSeller', { seller: _get(tempOffer, 'seller') }, { root: true })
        commit('setAddedOfferItem', true)
        return tempOffer
      })
      .catch(err => {
        throw err
      })
  },
  deleteOffer({ commit, getters }, payload: string): any {
    // offerItemId
    const path = `${BASE_URL}/offer-carts`
    const params = { id: payload }
    commit('setRequestDeleteOffer', true)
    return axios
      .delete(path, { params })
      .then(response => {
        commit('setActiveOffer', { offer: null })
        return true
      })
      .catch(err => {
        throw err
      })
      .finally(() => {
        commit('setRequestDeleteOffer', false)
      })
  },
  getOfferItems({ commit }, payload) {
    const path = `${BASE_URL}/offer-items`
    const params = {
      offerId: payload.offerId,
      limit: payload.limit,
      offset: payload.offset || 0,
      order: payload.order || [],
      filter: payload.filter || {},
      keyword: payload.keyword || '',
    }

    return axios
      .get(path, { params })
      .then(response => {
        const offerItems = _get(response, 'data')
        return offerItems
      })
      .catch(err => {
        throw err
      })
  },
  getOfferItemsCSV({ commit }, payload) {
    const path = `${BASE_URL}/offer-items/csv`
    const params = {
      offerId: payload.offerId,
      limit: payload.limit,
      offset: payload.offset || 0,
      order: payload.order || [],
      filter: payload.filter || {},
      keyword: payload.keyword || '',
    }

    return axios
      .get(path, { params })
      .then(response => {
        const offerItems = _get(response, 'data')
        return offerItems
      })
      .catch(err => {
        throw err
      })
  },
  postOfferItem({ commit, getters }, payload: { offerId: string; data: OfferItem }): any {
    const path = `${BASE_URL}/offer-items`
    commit('setRequestPostOfferItem', true)
    return axios
      .post(path, payload)
      .then(response => {
        const offerItem = _get(response, ['data', 'offerItem']) || null
        // const offerTotal = _get(response, ['data', 'offerTotal']) || 0;
        // const activeOffer = getters['/getActiveOffer'];
        commit('setRequestUploadInventoryList', false)
        commit('setAddedOfferItem', true)
        return offerItem
      })
      .catch(err => {
        throw err
      })
      .finally(() => {
        commit('setRequestPostOfferItem', false)
      })
  },
  putOfferItem({ commit, rootGetters }, payload: { id: string; data: OfferItem }): any {
    const path = `${BASE_URL}/offer-items`
    commit('setRequestPutOfferItem', payload.id)
    return axios
      .put(path, payload)
      .then(response => {
        const offerItem = _get(response, ['data', 'offerItem']) || null
        const offerTotal = _get(response, ['data', 'offerTotal']) || 0
        const activeOffer = rootGetters['offerCarts/getActiveOffer']
        commit('putOfferItem', { offerItemId: payload.id })
        commit('setActiveOffer', { offer: { ...activeOffer, offerTotal } })
        commit('setAddedOfferItem', true)
        return offerItem
      })
      .catch(err => {
        commit('setFailedPutOfferItem', payload.id)
        throw err
      })
  },
  deleteOfferItem({ commit }, payload: string): any {
    // offerItemId
    const path = `${BASE_URL}/offer-items`
    const params = { id: payload }
    commit('setRequestDeleteOfferItem', payload)
    return axios
      .delete(path, { params })
      .then(response => {
        commit('deleteOfferItem', payload)
        return true
      })
      .catch(err => {
        commit('setFailedDeleteOfferItem', payload)
        throw err
      })
  },
  postOfferComplete({ commit, rootGetters }, payload: string): any {
    // offerId
    const path = `${BASE_URL}/offer-cart/complete`
    commit('setRequestPostOfferComplete', true)
    return axios
      .post(path, { id: payload, userId: _get(rootGetters['auth/getUserInfo'], 'id') })
      .then(response => {
        commit('offerCompleted', { prevOfferId: payload, offer: _get(response, 'data') || null })
        return true
      })
      .catch(err => {
        commit('setRequestPostOfferComplete', false)
        throw err
      })
  },
  postOfferRevise({ commit, rootGetters }, payload: string): any {
    // offerId
    const path = `${BASE_URL}/offer-cart/revise`
    commit('setRequestPostOfferRevise', true)
    return axios
      .post(path, { id: payload, userId: _get(rootGetters['auth/getUserInfo'], 'id') })
      .then(response => {
        const data = _get(response, 'data') || null
        commit('offerRevised', { prevOfferId: payload, offer: data })
        return data
      })
      .catch(err => {
        commit('setRequestPostOfferRevise', false)
        throw err
      })
  },
  uploadInventoryList({ commit }, formData): any {
    const path = `${BASE_URL}/offer-cart/upload-inventory-list`
    commit('setRequestUploadInventoryList', true)
    return axios
      .post(path, formData)
      .then(response => {
        const data = _get(response, 'data')
        commit('setActiveOffer', { offer: data })
        return data
      })
      .catch(err => {
        throw err
      })
      .finally(() => {
        commit('setRequestUploadInventoryList', false)
      })
  },
}
